import React from 'react';
import { motion } from 'framer-motion';
import Button from '../Common/Button';
import './ItemSearchFilters.css';

interface FilterState {
  search: string;
  tags: string[];
  tiers: number[];
  levels: number[];
}

interface ItemSearchFiltersProps {
  filters: FilterState;
  onFiltersChange: (filters: FilterState) => void;
  availableTags: string[];
  characterTier?: number;
  characterLevel?: number;
  showTierFilter?: boolean;
  showLevelFilter?: boolean;
}

const ItemSearchFilters: React.FC<ItemSearchFiltersProps> = ({
  filters,
  onFiltersChange,
  availableTags,
  characterTier,
  characterLevel,
  showTierFilter = true,
  showLevelFilter = false,
}) => {
  const updateFilter = (key: keyof FilterState, value: any) => {
    onFiltersChange({
      ...filters,
      [key]: value,
    });
  };

  const toggleTag = (tag: string) => {
    const newTags = filters.tags.includes(tag)
      ? filters.tags.filter(t => t !== tag)
      : [...filters.tags, tag];
    updateFilter('tags', newTags);
  };

  const toggleTier = (tier: number) => {
    const newTiers = filters.tiers.includes(tier)
      ? filters.tiers.filter(t => t !== tier)
      : [...filters.tiers, tier];
    updateFilter('tiers', newTiers);
  };

  const toggleLevel = (level: number) => {
    const newLevels = filters.levels.includes(level)
      ? filters.levels.filter(l => l !== level)
      : [...filters.levels, level];
    updateFilter('levels', newLevels);
  };

  const getDefaultTiers = (): number[] => {
    if (characterTier) {
      // Return character tier + all lower tiers
      return Array.from({ length: characterTier }, (_, i) => i + 1);
    }
    // Default to Tier 1 if no character
    return [1];
  };

  const getDefaultLevels = (): number[] => {
    if (characterLevel) {
      // Return character level + all lower levels
      return Array.from({ length: characterLevel }, (_, i) => i + 1);
    }
    // Default to Level 1 if no character
    return [1];
  };

  const clearFilters = () => {
    onFiltersChange({
      search: '',
      tags: [],
      tiers: getDefaultTiers(),
      levels: getDefaultLevels(),
    });
  };

  const hasActiveFilters = filters.search || filters.tags.length > 0 || 
    (filters.tiers.length !== getDefaultTiers().length || 
     !filters.tiers.every(tier => getDefaultTiers().includes(tier))) ||
    (filters.levels.length !== getDefaultLevels().length || 
     !filters.levels.every(level => getDefaultLevels().includes(level)));

  return (
    <div className="item-search-filters">
      <div className="item-search-filters__header">
        <h3>Filters</h3>
        {hasActiveFilters && (
          <Button
            variant="TERTIARY"
            size="SMALL"
            onClick={clearFilters}
            className="item-search-filters__clear"
          >
            Clear All
          </Button>
        )}
      </div>

      <div className="item-search-filters__content">
        {/* Search */}
        <div className="filter-group">
          <label className="filter-group__label">Search</label>
          <input
            type="text"
            value={filters.search}
            onChange={(e) => updateFilter('search', e.target.value)}
            placeholder="Search by name, description, or tags..."
            className="filter-input"
          />
        </div>

        {/* Tier Filter - only show for categories with tiers */}
        {showTierFilter && (
          <div className="filter-group">
            <label className="filter-group__label">Tier</label>
            <div className="tier-filters">
              {[1, 2, 3, 4].map((tier) => (
                <button
                  key={tier}
                  onClick={() => toggleTier(tier)}
                  className={`tier-filter ${filters.tiers.includes(tier) ? 'tier-filter--active' : ''}`}
                >
                  Tier {tier}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Level Filter - for domain cards */}
        {showLevelFilter && (
          <div className="filter-group">
            <label className="filter-group__label">Level</label>
            <div className="level-filters">
              {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((level) => (
                <button
                  key={level}
                  onClick={() => toggleLevel(level)}
                  className={`level-filter ${filters.levels.includes(level) ? 'level-filter--active' : ''}`}
                >
                  {level}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Tags (Keep for legacy support but hidden by default) */}
        {availableTags.length > 0 && filters.tags.length > 0 && (
          <div className="filter-group">
            <label className="filter-group__label">Active Tags</label>
            <div className="tag-filters">
              {filters.tags.map((tag) => (
                <button
                  key={tag}
                  onClick={() => toggleTag(tag)}
                  className="tag-filter tag-filter--active"
                >
                  {tag}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ItemSearchFilters;